import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDefaultReleaseCover, getImageVariant } from '../../../lib/media';

interface CartItemProps {
  id: string;
  kind: 'release' | 'track';
  releaseId: number;
  releaseTitle?: string;
  trackId?: number;
  trackTitle?: string;
  previewTrackId?: number;
  title: string;
  artist: string;
  artistSlug: string;
  releaseSlug?: string;
  cover: string | null;
  price: number;
  isLimited?: boolean;
  limitedLeft?: number;
  isBundle?: boolean;
  isMemberOnly?: boolean;
  formats: string[];
  extras: string[];
  quantity: number;
  tipAmount?: number;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onSaveForLater: (id: string) => void;
  onAddTip?: (amount: number) => void;
  onRemoveTip?: () => void;
  onPreview: (options: { releaseSlug?: string; trackId?: number }) => void;
  isPreviewPlaying?: boolean;
  isPreviewLoading?: boolean;
}

const FALLBACK_COVER = getDefaultReleaseCover('small');

export const CartItem = ({
  id,
  kind,
  releaseTitle,
  trackTitle,
  trackId,
  previewTrackId,
  title,
  artist,
  artistSlug,
  releaseSlug,
  cover,
  price,
  isLimited,
  limitedLeft,
  isBundle,
  isMemberOnly,
  formats,
  extras,
  quantity,
  tipAmount,
  onUpdateQuantity,
  onRemove,
  onSaveForLater,
  onAddTip,
  onRemoveTip,
  onPreview,
  isPreviewPlaying = false,
  isPreviewLoading = false,
}: CartItemProps) => {
  const [isTipEditing, setIsTipEditing] = useState(false);
  const [tipInput, setTipInput] = useState('100');
  const canPreview = kind === 'track' ? Boolean(previewTrackId) : Boolean(releaseSlug);
  const hasTip = (tipAmount ?? 0) > 0;
  const coverSrc =
    (getImageVariant(cover, 'small') ?? cover)?.trim() || FALLBACK_COVER;

  const handleAddTip = (amount?: number) => {
    if (!onAddTip) return;
    const nextAmount = amount ?? Number(tipInput);
    if (!Number.isFinite(nextAmount) || nextAmount <= 0) return;
    onAddTip(nextAmount);
    setIsTipEditing(false);
  };

  useEffect(() => {
    if (hasTip && Number.isFinite(tipAmount)) {
      setIsTipEditing(false);
      setTipInput(String(tipAmount));
    }
  }, [hasTip, tipAmount]);

  const parsedTip = Number(tipInput);
  const isCustomTipValid = Number.isFinite(parsedTip) && parsedTip > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex gap-4">
        {/* Cover Image */}
        <div className="flex-shrink-0">
          <img
            src={coverSrc}
            alt={`${title} cover`}
            className="w-20 h-20 rounded-lg object-cover object-top"
            loading="lazy"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
              {kind === 'track' && releaseTitle && (
                <p className="text-xs text-gray-500">
                  From{' '}
                  {releaseSlug ? (
                    <Link
                      to={`/releases/${releaseSlug}`}
                      className="font-medium text-emerald-600 hover:text-emerald-700"
                    >
                      {releaseTitle}
                    </Link>
                  ) : (
                    <span className="font-medium">{releaseTitle}</span>
                  )}
                </p>
              )}
              <Link 
                to={`/artists/${artistSlug}`}
                className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                {artist}
              </Link>
            </div>
            
            {/* Preview Button */}
            <button
              onClick={() => canPreview && onPreview({ releaseSlug, trackId: previewTrackId ?? trackId })}
              className="p-2 text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={
                isPreviewPlaying
                  ? 'Pause preview'
                  : canPreview
                    ? 'Play preview'
                    : 'Preview unavailable'
              }
              disabled={!canPreview || isPreviewLoading}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {isPreviewLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className={isPreviewPlaying ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
                )}
              </div>
            </button>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            {isLimited && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Limited • {limitedLeft} left
              </span>
            )}
            {kind === 'track' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Track
              </span>
            )}
            {isBundle && kind === 'release' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Bundle
              </span>
            )}
            {isMemberOnly && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Member-only
              </span>
            )}
          </div>

          {/* Formats and Extras */}
          <div className="text-sm text-gray-600 mb-3">
            <div className="mb-1">
              <span className="font-medium">Formats:</span> {formats.join(' • ')}
            </div>
            {extras.length > 0 && (
              <div>
                <span className="font-medium">Extras:</span> {extras.join(' • ')}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="font-semibold text-gray-900">KES {price.toLocaleString()}</span>
              {isLimited && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Qty:</span>
                  <select
                    value={quantity}
                    onChange={(e) => onUpdateQuantity(id, Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
                  >
                    {Array.from({ length: Math.min(limitedLeft || 1, 5) }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onSaveForLater(id)}
                className="text-sm text-gray-500 hover:text-emerald-600 transition-colors cursor-pointer"
              >
                Save for later
              </button>
              <button
                onClick={() => onRemove(id)}
                className="text-sm text-red-600 hover:text-red-700 transition-colors cursor-pointer"
              >
                Remove
              </button>
            </div>
          </div>

          {/* Item Tip */}
          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <i className="ri-hand-heart-line text-emerald-600" />
                <span>Tip this {kind === 'track' ? 'track' : 'release'}</span>
                {hasTip && (
                  <span className="font-semibold text-emerald-700">
                    KES {tipAmount?.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasTip ? (
                  <>
                    <button
                      onClick={() => setIsTipEditing((prev) => !prev)}
                      className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                    >
                      Change
                    </button>
                    {onRemoveTip && (
                      <button
                        onClick={onRemoveTip}
                        className="text-sm text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                      >
                        Remove
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => setIsTipEditing((prev) => !prev)}
                    className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                  >
                    {isTipEditing ? 'Close' : 'Add tip'}
                  </button>
                )}
              </div>
            </div>

            {isTipEditing && (
              <div className="mt-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {[100, 250, 500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAddTip(amount)}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-emerald-300 hover:text-emerald-700 transition-colors cursor-pointer"
                    >
                      KES {amount.toLocaleString()}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">KES</span>
                  <input
                    type="number"
                    min={1}
                    value={tipInput}
                    onChange={(e) => setTipInput(e.target.value)}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="250"
                  />
                  <button
                    onClick={() => handleAddTip()}
                    disabled={!isCustomTipValid}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    Save tip
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
